import { useEffect, useRef } from 'react';

import {
  UpdateVideoRecordDocument,
  UpdateVideoRecordMutation,
  UpdateVideoRecordMutationVariables
} from '~/data';
import { client } from '~/data/client';
import { useTrainingStore } from '~/training/state';
import { useTrainingParams } from '~/training/useTrainingParams';
import { VideoComponentProps } from '~/training/sequence/templates/items/video/Video';
import { safely } from '~/lib/safely';
import { select } from '~/lib/select';
import { useMediaPlayerStore } from '~/ui/video-player/MediaPlayerProvider';

const useRestoreVideoWatchedSeconds = (
  videoComponentProps: VideoComponentProps
) => {
  const shouldSaveVideoWatchedSeconds = useTrainingStore(
    s => s.shouldSaveVideoWatchedSeconds
  );
  const { status: playerStatus, player: getPlayer } = useMediaPlayerStore(s =>
    select(s, 'status', 'player')
  );
  const areVideoWatchedSecondsRestoredRef = useRef(false);

  const watchedSeconds = videoComponentProps.videoRecord?.watchedSeconds;
  const player = getPlayer();

  const shouldRestoreVideoWatchedSeconds = () => {
    if (!shouldSaveVideoWatchedSeconds) return false;
    if (areVideoWatchedSecondsRestoredRef.current) return false;
    if (playerStatus !== 'ready' && playerStatus !== 'playing') return false;
    if (!watchedSeconds) return false;
    if (watchedSeconds <= 0) return false;
    if (!player) return false;

    return true;
  };

  const restoreVideoWatchedSeconds = safely(() => {
    if (!shouldRestoreVideoWatchedSeconds()) return;

    player!.one('canplay', () => {
      player!.currentTime(watchedSeconds!);
    });

    areVideoWatchedSecondsRestoredRef.current = true;
  });

  useEffect(restoreVideoWatchedSeconds, [playerStatus, watchedSeconds]);
};

const useAutoSaveVideoWatchedSeconds = (
  videoComponentProps: VideoComponentProps
) => {
  const { sequenceId } = useTrainingParams();
  const {
    learnerTrainingId,
    shouldSaveVideoWatchedSeconds,
    autoSaveVideoWatchedSecondsIntervalInSeconds: autoSaveIntervalInSeconds
  } = useTrainingStore(s =>
    select(
      s,
      'learnerTrainingId',
      'shouldSaveVideoWatchedSeconds',
      'autoSaveVideoWatchedSecondsIntervalInSeconds'
    )
  );
  const { status: playerStatus, currentTime: currentWatchedSeconds } =
    useMediaPlayerStore(s => select(s, 'status', 'currentTime'));
  const currentWatchedSecondsRef = useRef(currentWatchedSeconds);
  const lastSavedWatchedSecondsRef = useRef(0);

  const videoId = videoComponentProps.videoId?.toString();
  const autoSaveIntervalInMilliseconds = autoSaveIntervalInSeconds
    ? autoSaveIntervalInSeconds * 1000
    : 0;
  const autoSaveTimerRef = useRef<ReturnType<typeof setInterval>>();

  currentWatchedSecondsRef.current = currentWatchedSeconds;

  const shouldAutoSaveVideoWatchedSeconds = () => {
    if (!shouldSaveVideoWatchedSeconds) return false;
    if (!autoSaveIntervalInMilliseconds) return false;
    if (!sequenceId) return false;
    if (!videoId) return false;
    if (playerStatus !== 'playing') return false;

    return true;
  };

  const updateVideoRecord = safely((watchedSeconds: number) => {
    return client.mutate<
      UpdateVideoRecordMutation,
      UpdateVideoRecordMutationVariables
    >({
      mutation: UpdateVideoRecordDocument,
      variables: {
        learnerTrainingUuid: learnerTrainingId,
        sequenceId: sequenceId!,
        videoId: videoId!,
        watchedSeconds: Math.floor(watchedSeconds)
      }
    });
  });

  const autoSaveVideoWatchedSeconds = () => {
    if (!shouldAutoSaveVideoWatchedSeconds()) return;

    const watchedSeconds = currentWatchedSecondsRef.current;

    if (watchedSeconds === 0) return;
    if (watchedSeconds === lastSavedWatchedSecondsRef.current) return;

    lastSavedWatchedSecondsRef.current = watchedSeconds;

    updateVideoRecord(watchedSeconds);
  };

  const startAutoSaveVideoWatchedSeconds = () => {
    autoSaveTimerRef.current = setInterval(
      autoSaveVideoWatchedSeconds,
      autoSaveIntervalInMilliseconds
    );
  };

  const stopAutoSaveVideoWatchedSeconds = () => {
    if (autoSaveTimerRef.current) {
      clearInterval(autoSaveTimerRef.current);

      autoSaveTimerRef.current = undefined;
    }
  };

  useEffect(() => {
    if (!shouldAutoSaveVideoWatchedSeconds()) return;

    startAutoSaveVideoWatchedSeconds();

    return stopAutoSaveVideoWatchedSeconds;
  }, [
    playerStatus,
    learnerTrainingId,
    sequenceId,
    videoId,
    autoSaveIntervalInMilliseconds
  ]);
};

export const useSaveVideoWatchedSeconds = (
  videoComponentProps: VideoComponentProps
) => {
  useRestoreVideoWatchedSeconds(videoComponentProps);
  useAutoSaveVideoWatchedSeconds(videoComponentProps);
};
